// src/components/modals/DownloadModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { showToast } from '../../utils/helpers';
import api from "../../utils/api";
import { API_CONFIG } from '../../utils/apiConfig';

const DownloadModal = ({ 
  show, 
  onHide, 
  orderId, 
  productId,
  productName,
  productImage,
  onDownloadComplete
}) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState(null);
  
  const inputRefs = useRef([]);

  // Focus first input when modal opens
  useEffect(() => {
    if (show && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0].focus();
      }, 100);
    }
  }, [show]);

  const handlePinChange = (index, value) => {
    // Only allow numbers and limit to 1 character
    const numericValue = value.replace(/\D/g, '').slice(0, 1);
    
    const newPin = [...pin];
    newPin[index] = numericValue;
    setPin(newPin);

    // Auto-focus next input
    if (numericValue && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-submit when all digits are entered
    if (numericValue && index === 5) {
      const fullPin = newPin.join('');
      if (fullPin.length === 6) {
        handlePinSubmit(fullPin);
      }
    }

    // Clear error when user starts typing again
    if (error) {
      setError(null);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!pin[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1].focus();
      }
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numbers = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (numbers.length === 6) {
      const newPin = numbers.split('');
      setPin(newPin);
      
      // Focus the last input
      inputRefs.current[5].focus();
      
      // Auto-submit
      setTimeout(() => {
        handlePinSubmit(numbers);
      }, 100);
    }

    // Clear error on paste
    if (error) {
      setError(null);
    }
  };

  const handlePinSubmit = async (submittedPin = null) => {
    const pinToUse = submittedPin || pin.join('');
    
    if (!pinToUse || pinToUse.length !== 6) {
      showToast('Please enter a valid 6-digit PIN', 'error');
      return;
    }

    setIsVerifying(true);
    setError(null);
    setDownloadStatus('verifying');
    
    try {
  
      const response = await api.post(API_CONFIG.DOWNLOAD.DOWNLOAD_PIN_VERIFY, {
        orderId,
        productId,
        pin: pinToUse
      });


      if (response.data.success) {
        const downloadData = response.data.downloadData;
        setDownloadStatus('downloading');
        
        // Handle download for both external (Cloudinary) and local files
        await handleFileDownload(downloadData);
        
        // Notify parent component
        if (onDownloadComplete) {
          onDownloadComplete();
        }
        
      } else {
        throw new Error(response.data.error || 'Download failed');
      }
      
    } catch (error) {
      console.error('Error during PIN verification/download:', error);
      setDownloadStatus('error');
      
      // DO NOT clear PIN fields on error - let user see what they entered
      // Just focus the first input so they can easily edit
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
        inputRefs.current[0].select();
      }
      
      if (error.response?.data) {
        const errorMessage = error.response.data.error || error.message || 'Failed to verify PIN';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } else {
        const errorMessage = error.message || 'Network error during download';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsVerifying(false);
    }
  };

const handleFileDownload = async (downloadData) => {
  try {

    // Include publicId if available for authenticated Cloudinary URLs
    const params = {
        fileUrl: downloadData.fileUrl,
        fileName: downloadData.fileName || 'download'
    };
    
    if (downloadData.publicId) {
        params.publicId = downloadData.publicId;
        params.resourceType = downloadData.fileType || 'raw';
    }
    
    const response = await api.get(API_CONFIG.DOWNLOAD.DOWNLOAD_PROXY, {
      params: params,
      responseType: 'blob',
      timeout: 120000 // 2 minute timeout
    });

    // Create a blob URL from the response
    const blob = new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Create and trigger download link
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadData.fileName || 'download';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);
    
    showToast('Download started! Check your downloads folder.', 'success');
    setDownloadStatus('success');
    
    // Close modal after a short delay
    setTimeout(() => {
      handleClose();
    }, 2000);
    
  } catch (error) {
    console.error('Error downloading file via proxy:', error);
    
    let errorMessage = 'Failed to download file. Please try again.';
    
    if (error.response?.data) {
      if (error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || 'Download failed';
        } catch (parseError) {
          errorMessage = 'Download failed - invalid response from server';
        }
      } else {
        errorMessage = error.response.data.error || 'Download failed';
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'Download timeout. Please try again.';
    }
    
    throw new Error(errorMessage);
  }
};
  const handleClose = () => {
    setDownloadStatus(null);
    setError(null);
    setPin(['', '', '', '', '', '']); // Clear only on successful close
    setIsVerifying(false);
    onHide();
  };

  const handleResendPin = async () => {
    showToast('Please contact support if you need a new PIN', 'info');
  };

  const clearPin = () => {
    setPin(['', '', '', '', '', '']);
    setError(null);
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  if (!show) return null;

  const fullPin = pin.join('');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100 opacity-100">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {downloadStatus === 'success' ? 'Download Complete' : 'Enter PIN'}
          </h2>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl transition-colors"
            disabled={isVerifying}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Product Info */}
          <div className="flex items-center space-x-4 mb-6">
            {productImage && (
              <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={productImage} 
                  alt={productName}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {productName || 'Digital Product'}
              </h3>
              <p className="text-sm text-gray-600">
                {downloadStatus === 'success' 
                  ? 'Your download has been initiated!' 
                  : downloadStatus === 'downloading'
                  ? 'Starting download...'
                  : 'Enter the 6-digit PIN from your email'
                }
              </p>
            </div>
          </div>

          {/* PIN Verification Form - Show only when not completed */}
          {!['success', 'downloading'].includes(downloadStatus) && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="text-4xl mb-4">🔒</div>
                <h3 className="font-semibold text-gray-900 mb-2">Enter Download PIN</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Enter the 6-digit PIN sent to your email address
                </p>
              </div>

              {/* 6-Digit PIN Input Boxes */}
              <div className="space-y-4">
                <div className="flex justify-center space-x-2">
                  {pin.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => inputRefs.current[index] = el}
                      type="text"
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      onFocus={(e) => e.target.select()}
                      maxLength={1}
                      disabled={isVerifying}
                      className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:ring-2 focus:ring-blue-200 transition-colors ${
                        error 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      } ${isVerifying ? 'bg-gray-100' : 'bg-white'}`}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  ))}
                </div>

                <div className="flex justify-between items-center">
                  {/* Clear PIN Button */}
                  {fullPin.length > 0 && (
                    <button
                      type="button"
                      onClick={clearPin}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Clear PIN
                    </button>
                  )}
                  <div className="flex-1"></div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-700 text-sm text-center">{error}</p>
                    </div>
                  </div>
                )}

                {/* Manual Submit Button (fallback) */}
                <button
                  type="button"
                  onClick={() => handlePinSubmit()}
                  disabled={isVerifying || fullPin.length !== 6}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {isVerifying ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Verifying PIN...</span>
                    </>
                  ) : (
                    <span>Verify PIN & Download</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Downloading State */}
          {downloadStatus === 'downloading' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h4 className="font-semibold text-blue-900 mb-2">Starting Download</h4>
                <p className="text-blue-700 text-sm">
                  Your file is being prepared for download. Please wait...
                </p>
              </div>
            </div>
          )}

          {/* Success State */}
          {downloadStatus === 'success' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="font-semibold text-green-900 mb-2">Download Complete!</h4>
                <p className="text-green-700 text-sm">
                  Your file has been downloaded successfully. Check your downloads folder.
                </p>
              </div>

              <button
                onClick={handleClose}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Support Information */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Didn't receive the PIN? Check your spam folder or{' '}
              <button 
                onClick={handleResendPin}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                contact support
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;